import {useCallback, useEffect, useRef, useState} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {VideoElement} from '../../../../../../common/shared/utils/VideoElement.interface';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {LOG_CLICK_EVENT} from '../../../../../../logging/logEvent';
import {trackEvent} from '../../../../../../logging/trackEvent';
import {currentEventContext} from '../../../../../../logging/variables';
import {
  CONTROL_BOX_SHOW_TIME_MS,
  INI_VIDEO_PLAYER_CLASSNAME,
  VideoStatus,
} from '../constant/videoViewerConstant';
import {VideoPlayerControlBoxContainerProps} from '../container/VideoPlayerControlBoxContainer';
import {
  setIsSeeking as setIsSeekingAction,
  setShowControlBox,
  setVideoState,
} from '../state/videoViewerSlice';

export function useVideoViewerControl({
  videoRef,
  duration,
  setTimeUpdateHandlerEnabled,
  setCurrentTime,
  isShortsDrama,
  hasNextEpisode,
}: Pick<
  VideoPlayerControlBoxContainerProps,
  | 'videoRef'
  | 'duration'
  | 'setTimeUpdateHandlerEnabled'
  | 'setCurrentTime'
  | 'isShortsDrama'
  | 'hasNextEpisode'
>) {
  const isMobile = requestContext.requestInfo.display.isMobile;
  const {videoState, showControlBox, isHovered, isNonstopWatching} =
    useTypedSelector(state => {
      const videoState = state.videoViewer.videoState;
      const showControlBox = state.videoViewer.showControlBox;
      const isHovered = state.videoViewer.isHovered;
      const seriesId = state.viewerInfo.currentSeriesId;
      const isNonstopWatching =
        seriesId !== undefined
          ? !!state.viewerInfo.nonstopWatchingInSeries[seriesId]
              ?.isNonstopWatching
          : false;

      return {
        videoState,
        showControlBox,
        isHovered,
        isNonstopWatching,
      };
    });
  const page_meta = currentEventContext.currentPageView?.page_meta;
  const [isSeeking, setIsSeeking] = useState(false);
  const showControlBoxTimeoutId = useRef(0);
  // Indicator를 움직이기 전에 영상이 재생중이었는지 여부.
  const videoPlayingBeforeIndicatorSeeking = useRef(false);
  const dispatch = useTypedDispatch();

  // 커스텀 비디오 컨트롤을 일정 시간 뒤 닫게 만든다. 여러 번 실행되면 타이머가 초기화되어 다시 설정됨
  const setHideControlsTimer = useCallback(() => {
    window.clearTimeout(showControlBoxTimeoutId.current);
    // 재생중이면 2초뒤 컨트롤박스 숨김 PC에선 호버되어 있으면 그대로 유지
    if (videoState.videoStatus === VideoStatus.Playing && showControlBox) {
      showControlBoxTimeoutId.current = window.setTimeout(
        () => dispatch(setShowControlBox(false)),
        CONTROL_BOX_SHOW_TIME_MS
      );
    }
  }, [dispatch, showControlBox, videoState.videoStatus]);

  useEffect(() => {
    if (!isMobile && isHovered) return;
    setHideControlsTimer();
    return () => {
      clearTimeout(showControlBoxTimeoutId.current);
    };
  }, [isHovered, isMobile, setHideControlsTimer]);

  useEffect(() => {
    if (!isShortsDrama) {
      dispatch(setShowControlBox(true));
    }
    return () => {
      isShortsDrama && dispatch(setShowControlBox(false));
    };
  }, [dispatch, isShortsDrama]);

  const play = useCallback(() => {
    if (!videoRef.current) {
      return;
    }
    videoRef.current.play();
    const currentTimeWhenPlayStart = videoRef.current.currentTime;
    setTimeout(() => {
      if (!videoRef.current) {
        // 비디오 플레이버튼 누른 후 빠르게 다른 화면으로 이동하면 비디오 엘리먼트가 없을 수 있다
        return;
      }
      const changedCurrentTimeAfterPlay = videoRef.current.currentTime;
      if (currentTimeWhenPlayStart === changedCurrentTimeAfterPlay) {
        // 멈춤 상태일 때 currentTime 을 살짝만 조정하면 제대로 재생되는 것을 경험적으로 파악
        videoRef.current.currentTime = videoRef.current.currentTime + 0.001;
      }
    }, 1000);
    setHideControlsTimer();
  }, [setHideControlsTimer, videoRef]);

  const initialPlay = useCallback(() => {
    if (!videoRef.current) {
      return;
    }
    // 아무 소스가 없는 경우는 최초 재생 시도를 해야 데이터를 불러오기 시작하는 경우이므로, 이 때는 로딩을 띄워줌
    if (!videoRef.current.src) {
      dispatch(
        setVideoState({
          videoStatus: VideoStatus.Playing,
          isLoading: true,
        })
      );
    }
    setHideControlsTimer();
    videoRef.current.play();
  }, [dispatch, setHideControlsTimer, videoRef]);

  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, [videoRef]);

  const onClickMuteBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('음소거_클릭', e, () => {
        const video = videoRef.current;
        if (!video) {
          return;
        }
        if (video.volume === 0) {
          video.volume = 0.5;
          video.muted = false;
        } else {
          video.muted = !video.muted;
        }
        setHideControlsTimer();
      });
    },
    [setHideControlsTimer, videoRef]
  );

  const onClickPlayBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('재생_클릭', e, play);
    },
    [play]
  );

  const onClickInitialPlayBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('재생_클릭', e, initialPlay);
    },
    [initialPlay]
  );

  const onClickPauseBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('재생_클릭', e, pause);
    },
    [pause]
  );

  const onClickReplayBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('다시보기_클릭', e, () => {
        if (!videoRef.current) {
          return;
        }
        videoRef.current.currentTime = 0;
        videoRef.current.play();
        dispatch(
          setVideoState({
            videoStatus: VideoStatus.Playing,
          })
        );
        setHideControlsTimer();
      });
    },
    [dispatch, setHideControlsTimer, videoRef]
  );

  const onClickFullscreenBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('전체보기_클릭', e, () => {
        if (!videoRef.current) {
          return;
        }
        videoRef.current.openFullScreen();
      });
    },
    [videoRef]
  );

  const onChangeProgress = useCallback(
    (newProgress: number) => {
      if (!videoRef.current) {
        return;
      }

      setIsSeeking(true);

      const newCurrentTime = duration * newProgress;
      const videoEl = videoRef.current.getElementsByClassName(
        INI_VIDEO_PLAYER_CLASSNAME
      )[0] as VideoElement;
      // ini에서 자체적으로 어떤 처리를 하고 있는 것인지, 매우 적은 currentTime 변경이 일어나면 currentTime이 정상적으로 반영되지 않는다.
      // 프로그레스바를 움직이면 미세한 초 단위까지 움직일 수 있어야 하므로 직접 video Element에 currentTIme을 넣어줌.
      videoEl.currentTime = newCurrentTime;
      setCurrentTime(newCurrentTime);
    },
    [duration, setCurrentTime, videoRef]
  );

  const onReleaseProgressBar = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    setIsSeeking(false);

    const video = videoRef.current;
    // Progress Dot 이동 전에 비디오가 재생중이었다면, 비디오를 다시 재생시킨다.
    // 다만, 영상의 마지막으로 이동된 경우 play를 하면 영상이 다시 시작되므로 제외한다.
    if (
      videoPlayingBeforeIndicatorSeeking.current &&
      video.currentTime < duration
    ) {
      video.play();
    }
    videoPlayingBeforeIndicatorSeeking.current = false;

    // timeupdate 이벤트 핸들러 동작 재개
    setTimeUpdateHandlerEnabled(true);
    setHideControlsTimer();
  }, [duration, setHideControlsTimer, setTimeUpdateHandlerEnabled, videoRef]);

  const onPressProgressBar = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    const video = videoRef.current;

    // Indicator을 조작할 때는 비디오를 일시정지 시킨다.
    if (!video.paused) {
      videoPlayingBeforeIndicatorSeeking.current = true;
      video.pause();
    } else {
      videoPlayingBeforeIndicatorSeeking.current = false;
    }
    // timeupdate 이벤트 핸들러 동작 일시정지
    setTimeUpdateHandlerEnabled(false);
    window.clearTimeout(showControlBoxTimeoutId.current);
  }, [setTimeUpdateHandlerEnabled, videoRef]);

  const onClickPrevMoveBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('10초전_클릭', e, () => {
        if (!videoRef.current) {
          return;
        }

        const video = videoRef.current;
        videoRef.current.currentTime = Math.max(video.currentTime - 10, 0);
        setHideControlsTimer();
      });
    },
    [setHideControlsTimer, videoRef]
  );

  const onClickNextMoveBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      LOG_CLICK_EVENT('10초후_클릭', e, () => {
        if (!videoRef.current) {
          return;
        }

        const video = videoRef.current;
        videoRef.current.currentTime = Math.min(
          duration,
          video.currentTime + 10
        );
        setHideControlsTimer();
      });
    },
    [duration, setHideControlsTimer, videoRef]
  );

  const onChangeVolume = useCallback(
    (newVolume: number) => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      video.volume = newVolume;
      // 인디케이터를 잡고 움직일때 0이 아니라면 뮤트 상태를 풀어주어야 합니다.
      if (newVolume !== 0) {
        videoRef.current.muted = false;
      }
    },
    [videoRef]
  );

  const onClickTogglePlayback = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const videoStatusList = [
        VideoStatus.Paused,
        VideoStatus.BeforeInitialPlay,
      ];

      // 쇼츠 드라마일 경우 종료 상태에서도 재생 이외 영역 눌렀을 때 다시 play
      if (isShortsDrama) {
        videoStatusList.push(VideoStatus.Ended);
      }

      if (videoStatusList.includes(videoState.videoStatus)) {
        play();
        dispatch(setShowControlBox(false));
        trackEvent({
          section: '뷰어',
          page: '영상뷰어',
          actionType: 'Event',
          actionName:
            videoState.videoStatus === VideoStatus.Ended && isShortsDrama
              ? '다시보기_클릭'
              : '재생_클릭',
          click: {
            layer1: '뷰어메뉴',
            layer2: '재생컨트롤러',
            copy: '재생',
          },
          customProps: {
            user_action: 'click',
            viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
            is_shortdrama: isShortsDrama ? 'y' : undefined,
          },
          ...(page_meta && {page_meta}),
        });
      }

      if (videoState.videoStatus === VideoStatus.Playing) {
        pause();
        dispatch(setShowControlBox(true));
        trackEvent({
          section: '뷰어',
          page: '영상뷰어',
          actionType: 'Event',
          actionName: '재생_클릭',
          click: {
            layer1: '뷰어메뉴',
            layer2: '재생컨트롤러',
            copy: '일시정지',
          },
          customProps: {
            user_action: 'click',
            viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
            is_shortdrama: isShortsDrama ? 'y' : undefined,
          },
          ...(page_meta && {page_meta}),
        });
      }
    },
    [
      dispatch,
      play,
      pause,
      hasNextEpisode,
      isShortsDrama,
      videoState,
      page_meta,
      isNonstopWatching,
    ]
  );

  useEffect(() => {
    if (!isShortsDrama) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isSeeking) {
        onReleaseProgressBar();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onReleaseProgressBar, isShortsDrama, isSeeking]);

  useEffect(() => {
    dispatch(setIsSeekingAction(isSeeking));
  }, [dispatch, isSeeking]);

  return {
    setHideControlsTimer,
    onClickMuteBtn,
    onClickPlayBtn,
    onClickInitialPlayBtn,
    onClickPauseBtn,
    onClickReplayBtn,
    onClickFullscreenBtn,
    onChangeProgress,
    onReleaseProgressBar,
    onPressProgressBar,
    onClickPrevMoveBtn,
    onClickNextMoveBtn,
    onClickTogglePlayback,
    onChangeVolume,
    isSeeking,
  };
}
